import React, { useState, useEffect, useMemo } from 'react';
import { X, Scale, Image as ImageIcon, Loader2, Sparkles } from 'lucide-react';

const PATTERN_SCALE_MIN = 25;
const PATTERN_SCALE_MAX = 200;
const PATTERN_SCALE_DEFAULT = 100;

/**
 * Side-by-side kit + fabric preview with a slider to scale the print/pattern on the fabric swatch.
 * When `onApply` is provided, an "Apply to garment" action regenerates the garment
 * with the fabric motif rescaled to the chosen percentage.
 */
const PatternResizerModal = ({
  isOpen,
  onClose,
  kitImageUrl,
  kitName = 'Generated kit',
  fabricImageUrl,
  fabricName = 'Fabric',
  fabricSize = '',
  onApply,
  isApplying = false,
}) => {
  const [patternScale, setPatternScale] = useState(PATTERN_SCALE_DEFAULT);

  useEffect(() => {
    if (isOpen) {
      setPatternScale(PATTERN_SCALE_DEFAULT);
    }
  }, [isOpen, fabricImageUrl, kitImageUrl]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      // Don't dismiss mid-generation — the request is still running.
      if (e.key === 'Escape' && !isApplying) onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, isApplying]);

  const scaleLabel = useMemo(() => `${patternScale}%`, [patternScale]);

  const requestClose = () => {
    if (isApplying) return;
    onClose?.();
  };

  const handleApply = async () => {
    if (typeof onApply !== 'function' || isApplying) return;
    const ok = await onApply(patternScale);
    if (ok) onClose?.();
  };

  if (!isOpen) return null;

  // Full-screen blocking loader while the re-render request is in flight. It sits
  // above the modal (z higher than z-[80]) so the whole screen reads as busy and
  // no stray taps land on the garment/slider mid-generation.
  const fullScreenLoader = isApplying ? (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-5 bg-black/75 backdrop-blur-md px-6 text-center"
      role="alertdialog"
      aria-busy="true"
      aria-live="assertive"
      aria-label="Applying pattern scale"
    >
      <div className="relative flex items-center justify-center">
        <div className="absolute w-20 h-20 rounded-full bg-gradient-to-br from-purple-500/30 to-pink-500/30 blur-xl" />
        <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-2xl">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
      </div>
      <div className="max-w-xs space-y-1.5">
        <p className="text-base sm:text-lg font-bold text-white">Applying pattern scale…</p>
        <p className="text-xs sm:text-sm text-purple-100/90">
          Re-rendering your garment in high resolution with the fabric motif rescaled.
          This can take up to a minute — please keep this screen open.
        </p>
      </div>
      <div className="flex items-center gap-1.5" aria-hidden="true">
        <span className="w-2 h-2 rounded-full bg-white/80 animate-bounce" style={{ animationDelay: '-0.3s' }} />
        <span className="w-2 h-2 rounded-full bg-white/80 animate-bounce" style={{ animationDelay: '-0.15s' }} />
        <span className="w-2 h-2 rounded-full bg-white/80 animate-bounce" />
      </div>
    </div>
  ) : null;

  const hasKit = Boolean(kitImageUrl);
  const hasFabric = Boolean(fabricImageUrl);
  const isDefaultScale = patternScale === PATTERN_SCALE_DEFAULT;
  const canApply = typeof onApply === 'function' && hasKit && !isDefaultScale && !isApplying;

  return (
    <>
    {fullScreenLoader}
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6 bg-black/55 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pattern-resizer-title"
      onClick={requestClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start sm:items-center justify-between gap-3 p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 id="pattern-resizer-title" className="text-lg sm:text-xl font-bold text-gray-900">
                Pattern resizer
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                Compare your generated design with the source fabric and adjust pattern scale.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={isApplying}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Close pattern resizer"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">
          {/* Side-by-side on every size so the kit and fabric stay comparable —
              compact tiles on mobile, roomy on desktop. */}
          <div className="grid grid-cols-2 gap-3 sm:gap-6">
            <div className="flex flex-col min-h-0">
              <p className="text-[10px] sm:text-xs font-semibold text-purple-800 uppercase tracking-wide mb-1.5 sm:mb-2">
                Generated kit
              </p>
              <div className="flex-1 aspect-[3/4] sm:aspect-auto sm:min-h-[280px] rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50 to-indigo-50 overflow-hidden flex items-center justify-center">
                {hasKit ? (
                  <img
                    src={kitImageUrl}
                    alt={kitName}
                    className="max-w-full max-h-full sm:max-h-[min(50vh,420px)] w-auto h-auto object-contain"
                  />
                ) : (
                  <div className="text-center px-3 py-6 text-gray-500">
                    <ImageIcon className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 text-gray-400" />
                    <p className="text-xs sm:text-sm">No generated kit image available</p>
                  </div>
                )}
              </div>
              {kitName && hasKit && (
                <p className="text-[11px] sm:text-xs text-gray-600 mt-1.5 sm:mt-2 truncate" title={kitName}>
                  {kitName}
                </p>
              )}
            </div>

            <div className="flex flex-col min-h-0">
              <p className="text-[10px] sm:text-xs font-semibold text-purple-800 uppercase tracking-wide mb-1.5 sm:mb-2">
                Fabric
              </p>
              <div className="flex-1 aspect-[3/4] sm:aspect-auto sm:min-h-[280px] rounded-xl border border-purple-100 bg-gray-100 overflow-hidden relative">
                {hasFabric ? (
                  <div
                    className="absolute inset-0 transition-[background-size] duration-150 ease-out"
                    role="img"
                    aria-label={`${fabricName} pattern preview at ${scaleLabel}`}
                    style={{
                      backgroundImage: `url(${JSON.stringify(fabricImageUrl)})`,
                      backgroundRepeat: 'repeat',
                      backgroundPosition: 'center',
                      backgroundSize: `${patternScale}% auto`,
                    }}
                  />
                ) : (
                  <div className="text-center px-3 py-6 text-gray-500 flex flex-col items-center justify-center h-full">
                    <ImageIcon className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 text-gray-400" />
                    <p className="text-xs sm:text-sm font-medium text-gray-600">No source fabric linked to this kit</p>
                    <p className="text-[11px] sm:text-xs mt-1 max-w-[240px] mx-auto">
                      This design wasn&apos;t generated from a fabric swatch. You can still rescale the
                      motif — <strong>Apply to garment</strong> works using the pattern already in the design.
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-xs text-gray-600">
                {fabricName && hasFabric && (
                  <span className="truncate max-w-full" title={fabricName}>
                    {fabricName}
                  </span>
                )}
                {fabricSize && (
                  <span className="inline-flex items-center rounded-md bg-purple-50 text-purple-800 px-2 py-0.5 font-medium border border-purple-100">
                    Size: {fabricSize}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <label htmlFor="pattern-scale-slider" className="text-sm font-semibold text-gray-800">
                Pattern scale on fabric
              </label>
              <span className="text-sm font-bold text-purple-700 tabular-nums">{scaleLabel}</span>
            </div>
            <input
              id="pattern-scale-slider"
              type="range"
              min={PATTERN_SCALE_MIN}
              max={PATTERN_SCALE_MAX}
              step={1}
              value={patternScale}
              onChange={(e) => setPatternScale(Number(e.target.value))}
              disabled={!hasKit || isApplying}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-purple-600 disabled:opacity-40 disabled:cursor-not-allowed"
            />
            <div className="flex justify-between text-[10px] sm:text-xs text-gray-500">
              <span>Smaller motif ({PATTERN_SCALE_MIN}%)</span>
              <span>Default (100%)</span>
              <span>Larger motif ({PATTERN_SCALE_MAX}%)</span>
            </div>
            <p className="text-[11px] sm:text-xs text-gray-500">
              The swatch on the right previews the motif size. <strong>Apply to garment</strong> re-renders
              this exact design in <strong>high resolution</strong> with the fabric motif rescaled — same garment,
              fabric colour, weave and pose preserved. Uses image credits, like a normal AI edit.
            </p>
          </div>
        </div>

        <div className="p-3 sm:p-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
          {typeof onApply === 'function' && (
            <span className="text-[11px] sm:text-xs text-gray-500 sm:mr-auto">
              {isDefaultScale
                ? 'Move the slider away from 100% to apply a change.'
                : `Ready to re-render at ${scaleLabel}.`}
            </span>
          )}
          <button
            type="button"
            onClick={requestClose}
            disabled={isApplying}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {typeof onApply === 'function' ? 'Cancel' : 'Close'}
          </button>
          {typeof onApply === 'function' && (
            <button
              type="button"
              onClick={handleApply}
              disabled={!canApply}
              title={
                !hasKit
                  ? 'Import a kit with a generated image first'
                  : isDefaultScale
                    ? 'Adjust the pattern scale before applying'
                    : 'Re-render this garment with the fabric motif rescaled'
              }
              className={`w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-semibold rounded-lg flex items-center justify-center gap-2 transition-all ${
                canApply
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-sm'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isApplying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Applying…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Apply to garment
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default PatternResizerModal;
